import React, { useState, useEffect } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./reactDatepickerOverrides.css";
import { format } from "date-fns";

// Constantes para validación de rango
// NOTA: MAX_RANGE_DAYS se usa como fallback cuando no hay minDate/maxDate.
// Cuando hay minDate/maxDate, se debe usar la diferencia entre ellos para validar el rango.
const MAX_RANGE_DAYS = 90;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MIN_RANGE_DAYS = 2;

interface GenericDateTimeRangeValue {
	start: Date | null;
	end: Date | null;
}

interface GenericDateTimeRangePickerProps {
	label?: string;
	value: GenericDateTimeRangeValue;
	onChange?: (value: GenericDateTimeRangeValue) => void;
	displayFormat?: string;
	disabled?: boolean;
	className?: string;
	singleDate?: boolean;
	dateOnly?: boolean; // If true, disables time selection and sets time to midnight (00:00:00)
	minDate?: Date; // Minimum selectable date
	maxDate?: Date; // Maximum selectable date
	borderRadiusClass?: string; // 
}

const GenericDateTimeRangePicker: React.FC<GenericDateTimeRangePickerProps> = ({
	label = "Date Range",
	value,
	onChange,
	displayFormat = "dd/MM/yyyy HH:mm",
	disabled = false,
	className = "",
	singleDate = false,
	dateOnly = false,
	minDate,
	maxDate,
	borderRadiusClass,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	

	// Helper function to calculate days difference between two dates (ignoring time)
	// For dateOnly mode, this counts inclusive days (both start and end dates are included)
	// We must calculate this the same way getCalendarDays inside dateRangeUtils
	const getDaysDifference = (date1: Date, date2: Date): number => {
		const d1 = new Date(date1);
		d1.setHours(0, 0, 0, 0);
		const d2 = new Date(date2);
		d2.setHours(0, 0, 0, 0);
		
		// Usar componentes de fecha para cálculo consistente (igual que getCalendarDays)
		const year1 = d1.getFullYear();
		const month1 = d1.getMonth();
		const day1 = d1.getDate();
		
		const year2 = d2.getFullYear();
		const month2 = d2.getMonth();
		const day2 = d2.getDate();
		
		// Crear fechas en hora local (no UTC) para mantener consistencia
		const date1Local = new Date(year1, month1, day1);
		const date2Local = new Date(year2, month2, day2);
		
		// Calcular diferencia en milisegundos
		const diffMs = Math.abs(date2Local.getTime() - date1Local.getTime());
		const diffDays = diffMs / (1000 * 60 * 60 * 24);
		
		// Usar Math.floor para días completos y agregar 1 para contar ambos días como inclusivos
		return Math.floor(diffDays) + 1;
	};

	// Filter function simplificado: solo validaciones esenciales (min/max y mínimo 2 días)
	// La lógica de clamp/validate se hace en el componente padre
	const filterDate = (date: Date): boolean => {
		const dateOnly = new Date(date);
		dateOnly.setHours(0, 0, 0, 0);

		// Validar minDate/maxDate - esta es la validación principal
		// Si minDate está definido, solo permitir fechas >= minDate
		if (minDate) {
			const minDateOnly = new Date(minDate);
			minDateOnly.setHours(0, 0, 0, 0);
			if (dateOnly < minDateOnly) return false;
		}
		if (maxDate) {
			const maxDateOnly = new Date(maxDate);
			maxDateOnly.setHours(23, 59, 59, 999);
			if (date > maxDateOnly) return false;
		}

		if (singleDate) return true;

		// Calcular el rango máximo permitido basado en minDate y maxDate si están disponibles
		// Si hay minDate y maxDate, usar la diferencia entre ellos como límite
		let maxAllowedRangeDays = MAX_RANGE_DAYS;
		if (minDate && maxDate) {
			const minDateOnly = new Date(minDate);
			minDateOnly.setHours(0, 0, 0, 0);
			const maxDateOnly = new Date(maxDate);
			maxDateOnly.setHours(0, 0, 0, 0);
			maxAllowedRangeDays = getDaysDifference(minDateOnly, maxDateOnly);
		}

		// Si hay startDate seleccionado, validar contra startDate
		if (value.start && !value.end) {
			const startDateOnly = new Date(value.start);
			startDateOnly.setHours(0, 0, 0, 0);
			if (dateOnly < startDateOnly) return false; // endDate debe ser >= startDate
			if (dateOnly.getTime() === startDateOnly.getTime()) return false; // mínimo 2 días
			const daysDiff = getDaysDifference(value.start, date);
			if (daysDiff > maxAllowedRangeDays) return false;
		}

		// Si hay endDate seleccionado, validar contra endDate
		if (value.end && !value.start) {
			const endDateOnly = new Date(value.end);
			endDateOnly.setHours(0, 0, 0, 0);
			if (dateOnly > endDateOnly) return false; // startDate debe ser <= endDate
			if (dateOnly.getTime() === endDateOnly.getTime()) return false; // mínimo 2 días
			const daysDiff = getDaysDifference(date, value.end);
			if (daysDiff > maxAllowedRangeDays) return false;
		}

		// Si hay rango completo, permitir cualquier fecha válida (para empezar nuevo rango)
		// Ya validado contra minDate/maxDate arriba, así que solo verificar que esté dentro del rango permitido
		if (value.start && value.end) {
			// Ya validado contra minDate/maxDate arriba
			return true;
		}

		// Si no hay fechas seleccionadas, ya validado contra minDate/maxDate arriba
		return true;
	};

	// Muestra "start - end" o solo "start" para singleDate
	const getDisplayValue = () => {
		// When dateOnly is true, always use date-only format (no time)
		const dateFormat = dateOnly ? "dd/MM/yyyy" : displayFormat;
		if (singleDate) {
			return value.start ? format(value.start, dateFormat) : "";
		}
		if (!value.start && !value.end) return "";
		if (value.start && value.end) {
			return (
				format(value.start, dateFormat) +
				" - " +
				format(value.end, dateFormat)
			);
		}
		if (value.start) return format(value.start, dateFormat);
		if (value.end) return format(value.end, dateFormat);
		return "";
	};

	// Helper to set time to midnight (00:00:00) - only used when dateOnly is true
	const setToMidnight = (date: Date | null): Date | null => {
		if (!date) return null;
		const newDate = new Date(date);
		newDate.setHours(0, 0, 0, 0);
		return newDate;
	};

	// Handle date range selection
	const handleDateChange = (dates: Date | [Date | null, Date | null] | null) => {
		if (singleDate) {
			// For single date, dates will be a single Date object
			const date = dates as Date;
			const processedDate = dateOnly ? setToMidnight(date) : date;
			onChange?.({ start: processedDate, end: null });
			setIsOpen(false); // Close picker after single date selection
		} else {
			// For range, dates will be an array
			const [start, end] = dates as [Date | null, Date | null];
			const processedStart = dateOnly ? setToMidnight(start) : start;
			const processedEnd = dateOnly ? setToMidnight(end) : end;
			
			// Solo pasar las fechas al onChange, sin ajustes
			// El componente padre se encarga de clamp/validate usando normalizeDateRange
			onChange?.({ start: processedStart, end: processedEnd });
			
			// For range, only close if both dates are selected
			if (start && end) {
				setIsOpen(false);
			}
		}
	};

	// Close picker when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (!target.closest('.react-datepicker') && !target.closest('input')) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	return (
		<div className={`flex flex-col space-y-2 ${className}`}>
			{label && (
				<label className="text-sm font-medium text-semantic-text-secondary">
					{label}
				</label>
			)}

			<div className="relative">
				<input
					className={`
						w-full h-8 px-2 border border-semantic-border-secondary 
						${borderRadiusClass || "rounded-sm"} focus:outline-none focus:ring-2 focus:ring-semantic-text-brand 
						bg-semantic-bg-primary-alt text-semantic-text-secondary text-sm
						cursor-pointer
						${disabled ? "bg-gray-100 cursor-not-allowed" : ""}
					`}
					type="text"
					onClick={() => !disabled && setIsOpen(!isOpen)}
					value={getDisplayValue()}
					readOnly
					disabled={disabled}
					placeholder={singleDate ? "Select date..." : "Select date range..."}
				/>

				{isOpen && !disabled && (
					<div className="absolute z-50 mt-1 left-1/2 -translate-x-1/2 bg-white border border-gray-200 shadow-md rounded-md p-4">
						<ReactDatePicker
							selected={value.start && !value.end ? value.start : null}
							onChange={handleDateChange}
							startDate={value.start}
							endDate={singleDate ? null : value.end}
							selectsRange={!singleDate}
							openToDate={value.end || value.start || new Date()}
							inline
							monthsShown={1}
							showTimeSelect={false}
							showTimeSelectOnly={false}
							dateFormat="dd/MM/yyyy"
							calendarClassName="my-react-datepicker"
							minDate={minDate}
							maxDate={maxDate}
							filterDate={filterDate}
							dayClassName={(date) => {
								const dateOnly = new Date(date);
								dateOnly.setHours(0, 0, 0, 0);
								const dateMonth = date.getMonth();
								const dateYear = date.getFullYear();
								const dateDay = date.getDate();
								
								// Si no hay selección y el día coincide con maxDate pero no es el mes actual
								if (!value.start && !value.end && maxDate) {
									const maxDateOnly = new Date(maxDate);
									maxDateOnly.setHours(0, 0, 0, 0);
									// Solo ocultar si NO es el mismo día (fecha completa)
									if (dateOnly.getTime() !== maxDateOnly.getTime()) {
										const maxDateMonth = maxDate.getMonth();
										const maxDateYear = maxDate.getFullYear();
										const maxDateDay = maxDate.getDate();
										// Verificar si coincide el día del mes pero no el mes/año
										if (dateDay === maxDateDay && (dateMonth !== maxDateMonth || dateYear !== maxDateYear)) {
											return "hide-keyboard-selected";
										}
									}
								}
								
								// Si hay rango completo y el día coincide con endDate
								if (value.start && value.end && !singleDate) {
									const startOnly = new Date(value.start);
									startOnly.setHours(0, 0, 0, 0);
									const endOnly = new Date(value.end);
									endOnly.setHours(0, 0, 0, 0);
									
									// Verificar si el día está REALMENTE en el rango (fecha completa)
									const isInRange = dateOnly >= startOnly && dateOnly <= endOnly;
									
									const endMonth = value.end.getMonth();
									const endYear = value.end.getFullYear();
									const endDay = value.end.getDate();
									
									// Solo ocultar keyboard-selected si coincide el día pero NO está en el rango
									// Si está en el rango, debe mostrar los estilos de rango (in-range)
									if (dateDay === endDay && (dateMonth !== endMonth || dateYear !== endYear) && !isInRange) {
										return "hide-keyboard-selected";
									}
								}
								
								return null;
							}}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default GenericDateTimeRangePicker;
